import type { WorkLogEntry } from "../../session-logic";

import {
  describeBuiltInMorrowTool,
  resolveBuiltInWorkEntryIntegration,
} from "./morrowBuiltInToolDisplay";

export type WorkEntryStepSource = Pick<
  WorkLogEntry,
  | "label"
  | "detail"
  | "toolTitle"
  | "toolName"
  | "toolData"
  | "itemType"
  | "tone"
  | "requestKind"
  | "toolLifecycleStatus"
>;

export interface WorkEntryIntegration {
  slug: string;
  name: string;
}

export interface WorkEntryStep {
  heading: string;
  integration: WorkEntryIntegration | null;
}

const GENERIC_HEADINGS = new Set([
  "mcp tool call",
  "tool call",
  "item",
  "tool",
  "using tool",
  "web search",
  "websearch",
]);

/**
 * Known Composio / MCP toolkit slugs. Keys are normalized tokens that appear
 * in server names, tool names (`GMAIL_FETCH_EMAILS`), or titles (`gmail · …`).
 */
const INTEGRATION_ALIASES: Record<string, WorkEntryIntegration> = {
  gmail: { slug: "gmail", name: "Gmail" },
  googlecalendar: { slug: "googlecalendar", name: "Google Calendar" },
  google_calendar: { slug: "googlecalendar", name: "Google Calendar" },
  googledrive: { slug: "googledrive", name: "Google Drive" },
  google_drive: { slug: "googledrive", name: "Google Drive" },
  googledocs: { slug: "googledocs", name: "Google Docs" },
  google_docs: { slug: "googledocs", name: "Google Docs" },
  googlesheets: { slug: "googlesheets", name: "Google Sheets" },
  google_sheets: { slug: "googlesheets", name: "Google Sheets" },
  googlemeet: { slug: "googlemeet", name: "Google Meet" },
  google_meet: { slug: "googlemeet", name: "Google Meet" },
  slack: { slug: "slack", name: "Slack" },
  notion: { slug: "notion", name: "Notion" },
  github: { slug: "github", name: "GitHub" },
  linear: { slug: "linear", name: "Linear" },
  jira: { slug: "jira", name: "Jira" },
  trello: { slug: "trello", name: "Trello" },
  asana: { slug: "asana", name: "Asana" },
  hubspot: { slug: "hubspot", name: "HubSpot" },
  salesforce: { slug: "salesforce", name: "Salesforce" },
  stripe: { slug: "stripe", name: "Stripe" },
  discord: { slug: "discord", name: "Discord" },
  telegram: { slug: "telegram", name: "Telegram" },
  whatsapp: { slug: "whatsapp", name: "WhatsApp" },
  outlook: { slug: "outlook", name: "Outlook" },
  teams: { slug: "microsoft_teams", name: "Microsoft Teams" },
  microsoftteams: { slug: "microsoft_teams", name: "Microsoft Teams" },
  microsoft_teams: { slug: "microsoft_teams", name: "Microsoft Teams" },
  figma: { slug: "figma", name: "Figma" },
  canva: { slug: "canva", name: "Canva" },
  miro: { slug: "miro", name: "Miro" },
  zoom: { slug: "zoom", name: "Zoom" },
  composio_search: { slug: "composio_search", name: "Morrow" },
  composiosearch: { slug: "composio_search", name: "Morrow" },
  airtable: { slug: "airtable", name: "Airtable" },
  clickup: { slug: "clickup", name: "ClickUp" },
  zendesk: { slug: "zendesk", name: "Zendesk" },
  intercom: { slug: "intercom", name: "Intercom" },
  dropbox: { slug: "dropbox", name: "Dropbox" },
  linkedin: { slug: "linkedin", name: "LinkedIn" },
  twitter: { slug: "twitter", name: "X" },
  x: { slug: "twitter", name: "X" },
};

const GENERIC_SERVERS = new Set([
  "composio",
  "mcp",
  "t3-code",
  "t3code",
  "t3_code",
  "morrow",
  "morrowos",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function lookupIntegration(token: string): WorkEntryIntegration | null {
  const normalized = normalizeToken(token);
  if (!normalized || GENERIC_SERVERS.has(normalized)) {
    return null;
  }
  return INTEGRATION_ALIASES[normalized] ?? INTEGRATION_ALIASES[token.toLowerCase()] ?? null;
}

function splitTitleParts(value: string): { server: string | null; tool: string | null } {
  const dotted = value.split(" · ");
  if (dotted.length >= 2) {
    return { server: dotted[0]?.trim() || null, tool: dotted.slice(1).join(" · ").trim() || null };
  }
  const mcpMatch = /^mcp__([^_]+)__(.+)$/i.exec(value);
  if (mcpMatch) {
    return { server: mcpMatch[1] ?? null, tool: mcpMatch[2] ?? null };
  }
  return { server: null, tool: null };
}

function extractRawToolName(source: WorkEntryStepSource): string | null {
  if (source.toolName?.trim()) {
    return source.toolName.trim();
  }
  const data = asRecord(source.toolData);
  return (
    asTrimmedString(data?.tool) ?? asTrimmedString(data?.toolName) ?? asTrimmedString(data?.name)
  );
}

/** Tool slug/name on a work-log row — includes MCP payload fields when `toolName` is empty. */
export function resolveWorkEntryToolName(source: WorkEntryStepSource): string | null {
  return extractRawToolName(source);
}

function extractRawServer(source: WorkEntryStepSource): string | null {
  const data = asRecord(source.toolData);
  return asTrimmedString(data?.server) ?? asTrimmedString(data?.serverName);
}

function collectCandidateTokens(source: WorkEntryStepSource): string[] {
  const tokens: string[] = [];
  const server = extractRawServer(source);
  const toolName = extractRawToolName(source);
  if (server) tokens.push(server);
  if (toolName) tokens.push(toolName);

  for (const raw of [source.toolTitle, source.label, source.detail]) {
    if (!raw) continue;
    const parts = splitTitleParts(raw);
    if (parts.server) tokens.push(parts.server);
    if (parts.tool) tokens.push(parts.tool);
    const firstToken = raw.split(/[\s:]+/u)[0];
    if (firstToken) tokens.push(firstToken);
  }
  return tokens;
}

/** Longest-prefix match for Composio tool slugs (`GOOGLE_MEET_*`, `GOOGLEMEET_*`, `mcp__slack__*`). */
function resolveIntegrationFromToolName(toolName: string): WorkEntryIntegration | null {
  const trimmed = toolName.trim();
  if (!trimmed) {
    return null;
  }

  const titleParts = splitTitleParts(trimmed);
  if (titleParts.server) {
    const fromServer = lookupIntegration(titleParts.server);
    if (fromServer) {
      return fromServer;
    }
  }

  const toolBody = titleParts.tool ?? trimmed;
  const fromWhole = lookupIntegration(toolBody);
  if (fromWhole) {
    return fromWhole;
  }

  const segments = toolBody.split(/[_-]/u).filter(Boolean);
  for (let len = segments.length; len >= 1; len -= 1) {
    const underscored = segments.slice(0, len).join("_");
    const fromUnderscored = lookupIntegration(underscored);
    if (fromUnderscored) {
      return fromUnderscored;
    }
    const concatenated = segments.slice(0, len).join("");
    const fromConcatenated = lookupIntegration(concatenated);
    if (fromConcatenated) {
      return fromConcatenated;
    }
  }

  return null;
}

export function resolveWorkEntryIntegration(
  source: WorkEntryStepSource,
): WorkEntryIntegration | null {
  const rawTool = extractRawToolName(source);
  if (rawTool) {
    const fromBuiltIn = resolveBuiltInWorkEntryIntegration(rawTool);
    if (fromBuiltIn) {
      return fromBuiltIn;
    }
  }

  for (const token of collectCandidateTokens(source)) {
    const fromToken = lookupIntegration(token);
    if (fromToken) {
      return fromToken;
    }
    const fromPrefix = resolveIntegrationFromToolName(token);
    if (fromPrefix) {
      return fromPrefix;
    }
  }
  return null;
}

/** Scan prose for a known connected-app mention (e.g. "check Gmail"). */
export function resolveIntegrationMentionInText(text: string): WorkEntryIntegration | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const fromSource = resolveWorkEntryIntegration({
    label: trimmed,
    detail: trimmed,
    tone: "thinking",
  });
  if (fromSource) {
    return fromSource;
  }
  for (const match of trimmed.matchAll(/\b[A-Za-z][A-Za-z0-9_+-]*\b/g)) {
    const token = match[0];
    if (!token) {
      continue;
    }
    const fromToken = lookupIntegration(token);
    if (fromToken) {
      return fromToken;
    }
    const fromPrefix = resolveIntegrationFromToolName(token);
    if (fromPrefix) {
      return fromPrefix;
    }
  }
  return null;
}

const SPAWN_SUBAGENT_SLUG = "spawn_subagent";

function isSpawnSubagentTool(toolName: string): boolean {
  return normalizeToken(toolName.replace(/^\[subagent\]\s*/i, "")) === "spawnsubagent";
}

/** Composio web-search toolkit tools (`COMPOSIO_SEARCH`, `COMPOSIO_SEARCH_NEWS`, …). */
function isComposioSearchTool(toolName: string): boolean {
  const normalized = normalizeToken(toolName.replace(/^\[subagent\]\s*/i, ""));
  return normalized === "composiosearch" || normalized.startsWith("composiosearch");
}

function composioSearchAction(toolName: string): string {
  const cleaned = toolName.replace(/^\[subagent\]\s*/i, "").trim();
  const suffix = cleaned.replace(/^composio[_-]?search(?:[_-]|$)/i, "").trim();
  if (!suffix) {
    return "Searching the web";
  }
  const action = humanizeAction(suffix, null);
  if (!action) {
    return "Searching the web";
  }
  if (!/\bsearch\b/i.test(action)) {
    return `Search ${action.toLowerCase()}`;
  }
  return action;
}

function humanizeAction(raw: string, integration: WorkEntryIntegration | null): string {
  let value = raw.trim();
  const mcpParts = splitTitleParts(value);
  if (mcpParts.tool) {
    value = mcpParts.tool;
  }
  value = value.replace(/^mcp__/i, "");
  if (integration) {
    const prefixTokens = new Set<string>([integration.slug, integration.name.replace(/\s+/g, "")]);
    for (const [alias, entry] of Object.entries(INTEGRATION_ALIASES)) {
      if (entry.slug === integration.slug) {
        prefixTokens.add(alias);
        prefixTokens.add(alias.replace(/_/g, ""));
      }
    }
    const prefixAlternation = [...prefixTokens]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");
    const slugPattern = new RegExp(
      `^(?:${prefixAlternation}|${integration.name.replace(/\s+/g, "[_\\s-]*")})[_\\s-]*`,
      "i",
    );
    value = value.replace(slugPattern, "");
  }
  value = value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (value.length === 0) {
    return "";
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

function fallbackHeading(source: WorkEntryStepSource): string {
  if (source.itemType === "web_search") {
    return source.toolLifecycleStatus === "inProgress" ? "Searching the web" : "Searched the web";
  }
  if (source.requestKind === "file-read" || source.itemType === "image_view") {
    return source.toolLifecycleStatus === "inProgress" ? "Reading" : "Read";
  }
  if (source.requestKind === "file-change" || source.itemType === "file_change") {
    return source.toolLifecycleStatus === "inProgress" ? "Editing files" : "Edited files";
  }
  if (source.requestKind === "command" || source.itemType === "command_execution") {
    return source.toolLifecycleStatus === "inProgress" ? "Running a command" : "Ran a command";
  }
  if (source.tone === "thinking") {
    return source.label.trim() || "Thinking";
  }
  if (source.itemType === "mcp_tool_call") {
    return source.toolLifecycleStatus === "inProgress"
      ? "Using a connected app"
      : "Used a connected app";
  }
  return source.toolTitle?.trim() || source.label.trim() || "Working";
}

function isGenericHeading(value: string): boolean {
  return GENERIC_HEADINGS.has(value.trim().toLowerCase());
}

export function resolveWorkEntryStep(source: WorkEntryStepSource): WorkEntryStep {
  const integration = resolveWorkEntryIntegration(source);
  const rawTool = extractRawToolName(source);
  const titleParts = source.toolTitle
    ? splitTitleParts(source.toolTitle)
    : { server: null, tool: null };
  const actionSource = rawTool ?? titleParts.tool ?? source.toolTitle ?? source.label;
  const action = humanizeAction(actionSource, integration);

  if (integration) {
    const heading =
      action && !isGenericHeading(action) ? `${integration.name} · ${action}` : integration.name;
    return { heading, integration };
  }

  const preferred = source.toolTitle?.trim() || source.label.trim();
  if (preferred && !isGenericHeading(preferred)) {
    return { heading: preferred, integration: null };
  }

  return { heading: fallbackHeading(source), integration: null };
}

export interface ToolCallDescription {
  /** Human app name (e.g. "Google Calendar"), or null when unknown. */
  readonly appName: string | null;
  /** Humanized action (e.g. "Find event"), falls back to the cleaned slug. */
  readonly action: string;
  /** Composio logo URL for the app, or null when the app is unknown. */
  readonly logoUrl: string | null;
  /** Toolkit slug (e.g. "googlecalendar"), or null. */
  readonly slug: string | null;
}

/**
 * Describe a raw Composio tool slug (e.g. `GOOGLECALENDAR_FIND_EVENT`, optionally
 * `[subagent] `-prefixed) for compact UI: the app name + logo + a humanized
 * action label. Reuses the toolkit alias map and action humanizer.
 */
export function isMorrowNativeToolSlug(slug: string | null | undefined): boolean {
  return (
    slug === "composio_search" ||
    slug === SPAWN_SUBAGENT_SLUG ||
    slug === "morrow_memory" ||
    slug === "morrow_items"
  );
}

export function describeToolCall(toolName: string): ToolCallDescription {
  const cleaned = toolName.replace(/^\[subagent\]\s*/i, "").trim();
  const builtIn = describeBuiltInMorrowTool(cleaned);
  if (builtIn) {
    return builtIn;
  }
  if (isSpawnSubagentTool(cleaned)) {
    return {
      appName: null,
      action: "Spawn sub-agent",
      logoUrl: null,
      slug: SPAWN_SUBAGENT_SLUG,
    };
  }
  if (isComposioSearchTool(cleaned)) {
    return {
      appName: "Morrow",
      action: composioSearchAction(cleaned),
      logoUrl: null,
      slug: "composio_search",
    };
  }
  const integration = resolveIntegrationFromToolName(cleaned);
  if (integration?.slug === "composio_search") {
    return {
      appName: integration.name,
      action: composioSearchAction(cleaned),
      logoUrl: null,
      slug: integration.slug,
    };
  }
  const action = humanizeAction(cleaned, integration);
  return {
    appName: integration?.name ?? null,
    action: action.length > 0 ? action : cleaned,
    logoUrl: integration ? `https://logos.composio.dev/api/${integration.slug}` : null,
    slug: integration?.slug ?? null,
  };
}

export function workEntryPreviewLooksLikePayload(preview: string): boolean {
  const trimmed = preview.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[") || trimmed.startsWith("MCP call");
}
